// VIPListCard — VIP Breakthrough List for the Focus page (W5 demo).
//
// Reads/writes against `focus_vip_contacts` (migration 0030). RLS is
// owner-scoped SELECT/INSERT/UPDATE/DELETE. As of the persona build
// (2026-07-03) these contacts are LIVE for EMAIL urgency — the Email
// Triage classifier flags any email from a VIP as urgent regardless of
// content (see classify-worker loadVipEmails). The remaining piece,
// calendar focus-block breakthrough via the MacBook bridge, lands at
// Phase 1B (handover) — the copy distinguishes the two.
//
// Soft-delete on remove (sets deleted_at) — preserves audit trail.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, PhoneCall, ShieldCheck, X } from 'lucide-react';
import GlassCard from '@/components/shared/GlassCard';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/lib/auth';
import { supabase } from '@/lib/supabase';

type VIPContactRow = {
  id: string;
  name: string;
  role: string | null;
  email: string | null;
  phone_e164: string | null;
};

const SELECT_SHAPE = 'id, name, role, email, phone_e164';

async function fetchVips(): Promise<VIPContactRow[]> {
  const { data, error } = await supabase
    .from('focus_vip_contacts')
    .select(SELECT_SHAPE)
    .is('deleted_at', null)
    .order('name', { ascending: true });
  if (error) throw error;
  return (data as unknown as VIPContactRow[]) ?? [];
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

function contactLine(c: VIPContactRow): string {
  if (c.role) return c.role;
  if (c.email) return c.email;
  if (c.phone_e164) return c.phone_e164;
  return '—';
}

type VIPChipProps = {
  contact: VIPContactRow;
  onRemove: () => void;
  removing: boolean;
};

function VIPChip({ contact, onRemove, removing }: VIPChipProps): JSX.Element {
  return (
    <div
      className={`group relative flex items-center gap-3 py-2.5 px-3 rounded-md bg-secondary/40 border border-border hover:border-border/80 transition-all ${
        removing ? 'opacity-50' : ''
      }`}
    >
      <div className="w-8 h-8 rounded-full bg-forest-soft flex items-center justify-center text-xs font-medium text-forest shrink-0">
        {initials(contact.name)}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-foreground truncate">{contact.name}</p>
        <p className="text-[11px] text-muted-foreground truncate">{contactLine(contact)}</p>
      </div>
      <button
        type="button"
        onClick={onRemove}
        disabled={removing}
        aria-label={`Remove ${contact.name} from VIP list`}
        className="shrink-0 w-5 h-5 rounded-full opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity flex items-center justify-center text-muted-foreground hover:text-alert"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

type AddFormProps = {
  ownerId: string;
  onDone: () => void;
};

function AddForm({ ownerId, onDone }: AddFormProps): JSX.Element {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [role, setRole] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: async () => {
      const trimmedName = name.trim();
      if (!trimmedName) throw new Error('Name required.');
      if (!email.trim() && !phone.trim()) {
        throw new Error('Provide an email or phone number so the bridge can match.');
      }
      // Lowercase email at insert — the partial UNIQUE index in
      // migration 0030 dedups on LOWER(email). Without normalizing
      // here `Foo@Bar.com` and `foo@bar.com` would collide and surface
      // as a cryptic 23505 from Postgres. Normalize once, fail clearly.
      const normalizedEmail = email.trim().toLowerCase() || null;
      const { error: insErr } = await supabase.from('focus_vip_contacts').insert({
        owner_id: ownerId,
        name: trimmedName,
        role: role.trim() || null,
        email: normalizedEmail,
        phone_e164: phone.trim() || null,
      });
      if (insErr) {
        // Translate the canonical "unique_violation" Postgres code into
        // a user-readable message. supabase-js surfaces this on
        // `error.code` ('23505') for partial-UNIQUE collisions.
        if ((insErr as { code?: string }).code === '23505') {
          throw new Error('A VIP with that email or phone is already on your list.');
        }
        throw insErr;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['focus_vips'] });
      onDone();
    },
    onError: (err: Error) => setError(err.message),
  });

  return (
    <div className="space-y-3 rounded-md border border-border bg-secondary/40 p-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <div>
          <Label htmlFor="vip-name" className="text-[10px] uppercase tracking-wide">
            Name
          </Label>
          <Input
            id="vip-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Mani K."
            maxLength={80}
            className="mt-1"
          />
        </div>
        <div>
          <Label htmlFor="vip-role" className="text-[10px] uppercase tracking-wide">
            Role (optional)
          </Label>
          <Input
            id="vip-role"
            value={role}
            onChange={(e) => setRole(e.target.value)}
            placeholder="Co-founder"
            maxLength={80}
            className="mt-1"
          />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <div>
          <Label htmlFor="vip-email" className="text-[10px] uppercase tracking-wide">
            Email
          </Label>
          <Input
            id="vip-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="name@example.com"
            className="mt-1 font-mono text-xs"
          />
        </div>
        <div>
          <Label htmlFor="vip-phone" className="text-[10px] uppercase tracking-wide">
            Phone (E.164)
          </Label>
          <Input
            id="vip-phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+14155551234"
            className="mt-1 font-mono text-xs"
          />
        </div>
      </div>
      {error && (
        <p className="text-xs text-alert" role="alert">
          {error}
        </p>
      )}
      <div className="flex items-center justify-end gap-2">
        <Button type="button" variant="outline" size="sm" onClick={onDone}>
          Cancel
        </Button>
        <Button type="button" size="sm" onClick={() => create.mutate()} disabled={create.isPending}>
          {create.isPending && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
          Add VIP
        </Button>
      </div>
    </div>
  );
}

export default function VIPListCard(): JSX.Element | null {
  const { user } = useAuth();
  const qc = useQueryClient();
  const vipsQ = useQuery({ queryKey: ['focus_vips'], queryFn: fetchVips });
  const [showAdd, setShowAdd] = useState(false);

  const [removeError, setRemoveError] = useState<string | null>(null);

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('focus_vip_contacts')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      setRemoveError(null);
      qc.invalidateQueries({ queryKey: ['focus_vips'] });
    },
    onError: (err: Error) => setRemoveError(err.message),
  });

  if (!user) return null;
  const vips = vipsQ.data ?? [];

  return (
    <GlassCard hover={false}>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <PhoneCall className="w-4 h-4 text-forest" aria-hidden />
          <h2 className="text-sm font-medium text-foreground">VIP breakthrough list</h2>
        </div>
        <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-forest/10 text-forest">
          Email urgency: live
        </span>
      </div>
      <p className="text-xs text-muted-foreground mb-3">
        Email from these contacts is <strong>always flagged urgent</strong> in triage now, no matter
        the content. Calendar focus-block breakthrough (via the MacBook bridge) lands at handover
        (Phase 1B); until then, add your VIPs here so email escalation applies immediately.
      </p>

      {vipsQ.isLoading && <p className="text-xs text-muted-foreground">Loading…</p>}
      {vipsQ.isError && (
        <p className="text-xs text-alert">Failed to load: {(vipsQ.error as Error).message}</p>
      )}

      {vips.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
          {vips.map((c) => (
            <VIPChip
              key={c.id}
              contact={c}
              onRemove={() => remove.mutate(c.id)}
              removing={remove.isPending && remove.variables === c.id}
            />
          ))}
        </div>
      )}

      {vips.length === 0 && !vipsQ.isLoading && !showAdd && (
        <p className="text-xs text-muted-foreground mb-3 italic">
          No VIPs yet. Add the contacts whose calls must reach you anytime.
        </p>
      )}

      {removeError && (
        <p
          className="text-xs text-alert mb-3 rounded-md bg-alert/5 border border-alert/20 px-3 py-2"
          role="alert"
        >
          Couldn&apos;t remove VIP: {removeError}
        </p>
      )}

      {showAdd ? (
        <AddForm ownerId={user.id} onDone={() => setShowAdd(false)} />
      ) : (
        <Button type="button" variant="outline" size="sm" onClick={() => setShowAdd(true)}>
          + Add contact
        </Button>
      )}

      <p className="mt-3 text-[10px] text-muted-foreground/80 flex items-center gap-1">
        <ShieldCheck className="w-3 h-3 inline" />
        Stored under owner-scoped RLS. Nothing leaves Joel&apos;s Supabase.
      </p>
    </GlassCard>
  );
}
